// Translator notes: Input as typed: spoken Finnish, clitics, casual English, typos, HV letters, "you" (engine/src/input-fixes.ts).
// Each id is an engine note (`notes.add(text, id, args)`); `args` names its arguments in the order the
// strings take them. Collected by translator/notes.ts (import.meta.glob): adding a note means adding it here.
import { defineNotes } from '../note-defs';

export const notes = defineNotes({
  fiSpoken: {
    args: ['pairs'],
    fi: (a: string[]) => `Puhekieli luettiin yleiskielenä: ${a[0]}.`,
    en: (a: string[]) => `Spoken Finnish was read as standard Finnish: ${a[0]}.`,
  },
  fiClitic: {
    args: ['pairs'],
    fi: (a: string[]) => `Liitepartikkeli käännettiin omana sananaan: ${a[0]}.`,
    en: (a: string[]) => `A clitic particle was translated as a word of its own: ${a[0]}.`,
  },
  fiCliticDropped: {
    args: ['pairs'],
    fi: (a: string[]) => `Korostava liitepartikkeli jäi kääntämättä, koska valyriassa ei ole sille sanaa: ${a[0]}.`,
    en: (a: string[]) => `An emphatic clitic particle was not translated, because Valyrian has no word for it: ${a[0]}.`,
  },
  enCasual: {
    args: ['pairs'],
    fi: (a: string[]) => `Arkinen englanti luettiin yleiskielenä: ${a[0]}.`,
    en: (a: string[]) => `Casual English was read as standard English: ${a[0]}.`,
  },
  enTypo: {
    args: ['words', 'pairs', 'alternatives'],
    fi: (a: string[]) => `Tarkoititko ${a[0]}? Käännös tehtiin korjatusta sanasta (${a[1]})${a[2] ? `. Myös mahdollinen: ${a[2]}` : ''}.`,
    en: (a: string[]) => `Did you mean ${a[0]}? The corrected word was translated (${a[1]})${a[2] ? `. Also possible: ${a[2]}` : ''}.`,
  },
  hvTypo: {
    args: ['words', 'pairs', 'alternatives'],
    fi: (a: string[]) =>
      `Tarkoititko ${a[0]}? Kirjoitettua muotoa ei ole sanakirjassa, joten käännös tehtiin lähimmästä oikeasta muodosta (${a[1]})${a[2] ? `. Myös mahdollinen: ${a[2]}` : ''}.`,
    en: (a: string[]) =>
      `Did you mean ${a[0]}? The form as written is not in the dictionary, so the closest real form was translated (${a[1]})${a[2] ? `. Also possible: ${a[2]}` : ''}.`,
  },
  hvLetters: {
    // alternatives: other real spellings of the same letters (build-068: ilas → ilās, myös īlās)
    args: ['pairs', 'alternatives'],
    fi: (a: string[]) => `Sanat luettiin valyrian kirjaimin (pitkät vokaalit, ñ): ${a[0]}.${a[1] ? ` Myös mahdollinen: ${a[1]}.` : ''}`,
    en: (a: string[]) => `The words were read with Valyrian letters (long vowels, ñ): ${a[0]}.${a[1] ? ` Also possible: ${a[1]}.` : ''}`,
  },
  youDefault: {
    args: [],
    fi: () => 'Englannin ”you” luettiin yksiköksi (sinä, valyriaksi ao). Jos puhuttelet useampaa, valitse ”te”.',
    en: () => 'English “you” was read as one person (Valyrian ao). If you are addressing several people, choose “te”.',
  },
  youCue: {
    args: ['cue'],
    fi: (a: string[]) => `Englannin ”you” luettiin monikoksi (te, valyriaksi jeme), koska tekstissä on ”${a[0]}”.`,
    en: (a: string[]) => `English “you” was read as several people (Valyrian jeme), because the text says “${a[0]}”.`,
  },
  youChosenSg: {
    args: [],
    fi: () => 'Englannin ”you” luettiin yksiköksi (sinä, valyriaksi ao), kuten valitsit.',
    en: () => 'English “you” was read as one person (Valyrian ao), as you chose.',
  },
  youChosenPl: {
    args: [],
    fi: () => 'Englannin ”you” luettiin monikoksi (te, valyriaksi jeme), kuten valitsit.',
    en: () => 'English “you” was read as several people (Valyrian jeme), as you chose.',
  },
});

declare module '../note-defs' {
  interface NoteIds extends Record<keyof typeof notes, true> {}
}
